
'use client';

import type React from 'react';
import type { AuditState, AuditCategory, AuditFinding } from '@/app/actions';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { AlertTriangle, CheckCircle2, Info, TrendingUp, Accessibility, ShieldCheck, SearchCheck, ThumbsUp } from 'lucide-react';

// Helper to render markdown links as actual anchor tags
const renderTextWithLinks = (text: string): React.ReactNode => {
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    // Add the link
    parts.push(
      <a
        key={match.index}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline hover:text-primary/80"
      >
        {match[1]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

interface AuditResultsDisplayProps {
  auditState: AuditState;
}

const getScoreColor = (score: number | null): string => {
  if (score === null) return 'bg-muted'; // Neutral for null scores
  if (score >= 0.9) return 'bg-green-500'; // Green for 90-100
  if (score >= 0.5) return 'bg-yellow-500'; // Yellow for 50-89
  return 'bg-red-500'; // Red for 0-49
};

const CategoryDisplay: React.FC<{ category?: AuditCategory; icon: React.ReactNode }> = ({ category, icon }) => {
  if (!category || category.score === null) {
    return (
      <Card className="shadow-md">
        <CardHeader>
          <div className="flex items-center space-x-3">
            {icon}
            <CardTitle className="text-xl font-headline">{category?.title || 'Data Unavailable'}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Audit data for this category is not available.</p>
        </CardContent>
      </Card>
    );
  }

  const displayScore = Math.round(category.score * 100);

  return (
    <Card className="shadow-md">
      <CardHeader>
        <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
                {icon}
                <CardTitle className="text-xl font-headline">{category.title}</CardTitle>
            </div>
            <span className={`text-2xl font-bold ${getScoreColor(category.score).replace('bg-', 'text-')}`}>{displayScore}</span>
        </div>
        <Progress value={displayScore} indicatorClassName={getScoreColor(category.score)} className="mt-2 h-3" />
        {category.description && <CardDescription className="mt-2 break-words">{renderTextWithLinks(category.description)}</CardDescription>}
      </CardHeader>
      <CardContent>
        {category.findings && category.findings.length > 0 ? (
          <Accordion type="single" collapsible className="w-full">
            {category.findings.map((finding) => (
              <AccordionItem value={finding.id} key={finding.id}>
                <AccordionTrigger>
                  <div className="flex items-center justify-between w-full">
                    <span className="text-left flex-1 mr-2">{finding.title}</span>
                    {finding.score !== null && ( // Individual finding scores might be 0, so compare to null
                       <span
                        className={`text-sm font-semibold px-2 py-1 rounded-md text-white ${getScoreColor(finding.score)}`}
                       >
                        {Math.round(finding.score * 100)}
                       </span>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-muted-foreground whitespace-pre-line break-words mb-2">{renderTextWithLinks(finding.description)}</p>
                  {finding.displayValue && <p className="text-sm"><strong>Value:</strong> {finding.displayValue}</p>}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          (() => {
            const score = category.score; // category.score is non-null here
            const scoreColorClass = getScoreColor(score).replace('bg-', 'text-');
            if (score >= 0.9) {
              return (
                <div className={`flex items-center ${scoreColorClass}`}>
                  <ThumbsUp className="h-5 w-5 mr-2" />
                  <p>Excellent! No specific actionable items listed and a high score in this category.</p>
                </div>
              );
            } else if (score >= 0.5) {
              return (
                <div className={`flex items-center ${scoreColorClass}`}>
                  <CheckCircle2 className="h-5 w-5 mr-2" />
                  <p>Good. No specific actionable items are listed from the audit for this category.</p>
                </div>
              );
            } else { // score < 0.5
              return (
                <div className={`flex items-center ${scoreColorClass}`}>
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  <p>Attention needed. While no specific actionable items are listed, the score indicates issues in this category.</p>
                </div>
              );
            }
          })()
        )}
      </CardContent>
    </Card>
  );
};


export function AuditResultsDisplay({ auditState }: AuditResultsDisplayProps) {
  if (auditState.error) {
    return (
      <Card className="mt-8 w-full bg-destructive/10 border-destructive shadow-lg">
        <CardHeader>
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-8 w-8 text-destructive" />
            <CardTitle className="text-2xl font-headline text-destructive">Analysis Error</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-destructive-foreground">{auditState.error}</p>
          {auditState.url && <p className="text-sm text-destructive-foreground mt-2">URL: {auditState.url}</p>}
        </CardContent>
      </Card>
    );
  }

  if (!auditState.categories || Object.keys(auditState.categories).length === 0) {
    return (
      <Card className="mt-8 w-full bg-card shadow-lg">
        <CardHeader>
          <div className="flex items-center space-x-3">
            <Info className="h-8 w-8 text-primary" />
            <CardTitle className="text-2xl font-headline">No Audit Data</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Audit data could not be retrieved or processed for the URL: {auditState.url}. This might be due to an invalid URL, a site that blocks automated tools, or an issue with the PageSpeed service.</p>
        </CardContent>
      </Card>
    );
  }
  
  const { performance, accessibility, bestPractices, seo } = auditState.categories;

  return (
    <div className="mt-8 space-y-8">
      <Card className="bg-card shadow-lg">
        <CardHeader>
            <CardTitle className="text-2xl font-headline">Audit Summary for: <span className="text-primary break-all">{auditState.url}</span></CardTitle>
            {auditState.timestamp && <CardDescription>Analyzed on: {new Date(auditState.timestamp).toLocaleString()}</CardDescription>}
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CategoryDisplay category={performance} icon={<TrendingUp className="h-6 w-6 text-primary" />} />
        <CategoryDisplay category={accessibility} icon={<Accessibility className="h-6 w-6 text-primary" />} />
        <CategoryDisplay category={bestPractices} icon={<ShieldCheck className="h-6 w-6 text-primary" />} />
        <CategoryDisplay category={seo} icon={<SearchCheck className="h-6 w-6 text-primary" />} />
      </div>
    </div>
  );
}
